import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, type TomEtiqueta } from '../comum';
import { Anomalias } from './Anomalias';

/**
 * Problemas do parque, com o que fazer sobre cada um.
 *
 * 🔴 **A tela era um `JSON.stringify` da resposta.** Funcionava para depurar e
 * não servia para operar: quem abre esta aba precisa decidir o que atacar
 * primeiro, e um bloco de JSON obriga a pessoa a fazer o trabalho de leitura que
 * o produto deveria ter feito.
 *
 * Cada problema aparece com severidade, alcance, tendência e **a recomendação**.
 * A recomendação é determinística — sai da tabela fechada do domínio, com
 * validador que recusa evidência inventada ou automação prometida a mais.
 */

interface Recomendacao {
  readonly problemId: string;
  readonly acao: string;
  readonly titulo: string;
  readonly justificativa: string;
  readonly automatizavel: boolean;
  readonly capacidade: string | null;
  readonly severidade: string;
}

interface Resposta {
  readonly dia: string | null;
  readonly itens: readonly Recomendacao[];
  readonly recusadas: readonly {
    readonly problemId: string;
    readonly erros: readonly { readonly campo: string; readonly mensagem: string }[];
  }[];
}

const SEVERIDADE: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta }>> = {
  CRITICAL: { rotulo: 'Crítico', tom: 'erro' },
  HIGH: { rotulo: 'Alto', tom: 'erro' },
  MEDIUM: { rotulo: 'Médio', tom: 'alerta' },
  LOW: { rotulo: 'Baixo', tom: 'neutro' },
};

const ACAO: Readonly<Record<string, string>> = {
  forcar_inventario: 'Forçar inventário',
  revisar_patches: 'Revisar patches',
  liberar_espaco: 'Liberar espaço',
  revisar_conformidade: 'Revisar conformidade',
  reinstalar_agente: 'Verificar o agente',
  investigar_manualmente: 'Investigar manualmente',
};

export function Problemas({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Resposta>('/api/recomendacoes'));
      setErro(null);
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
      else setErro(e instanceof Error ? e.message : String(e));
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <div className="tela">
      <div className="cabecalho-tela">
        <h1>Problemas</h1>
        <p>Ranking determinístico, com evidências do ambiente e o que fazer sobre cada um.</p>
      </div>

      <Anomalias aoPerderSessao={aoPerderSessao} />

      <Painel
        titulo="Problemas e recomendações"
        sub={
          dados?.dia == null
            ? 'Sem briefing gerado ainda.'
            : `${idadeDoRetrato(dados.dia)} · ${data(dados.dia)}.`
        }
      >
        {erro !== null && <Aviso tipo="erro">{erro}</Aviso>}

        {dados !== null && dados.itens.length === 0 && (
          <p className="suave">
            {dados.dia === null
              ? 'O briefing diário ainda não rodou — sem ele não há ranking.'
              : 'Nenhum problema detectado no último retrato.'}
          </p>
        )}

        {(dados?.itens ?? []).map((r) => {
          const sev = SEVERIDADE[r.severidade] ?? { rotulo: r.severidade, tom: 'neutro' as const };
          return (
            <div
              key={r.problemId}
              style={{
                borderBottom: '1px solid var(--borda)',
                paddingBottom: 12,
                marginBottom: 12,
              }}
            >
              {/*
                🔴 O negrito era a CATEGORIA da ação, e ela se repete.
                Três problemas de patch mostravam "Revisar patches" três vezes
                em destaque, e o que os distinguia ficava na linha apagada
                embaixo. O olho lê o negrito primeiro e via três itens iguais.
                Agora o destaque é o que muda; a categoria vira etiqueta.
              */}
              <div className="linha">
                <strong>{r.titulo}</strong>
                <Etiqueta tom={sev.tom}>{sev.rotulo}</Etiqueta>
              </div>
              <p className="suave">{ACAO[r.acao] ?? r.acao}</p>
              {/*
                A justificativa é montada a partir das evidências do detector —
                não é texto de IA. Se a evidência mudar, a frase muda junto.
              */}
              <p className="suave">{r.justificativa}</p>
              <p className="suave">
                {r.automatizavel ? (
                  <Etiqueta tom="ok">o produto pode executar</Etiqueta>
                ) : (
                  <>
                    Ação humana
                    {/*
                      🔴 Mostrava o nome interno: "depende de patch.read".
                      Quem opera o produto não sabe o que é `patch.read`, e a
                      frase não dizia o que fazer com a informação.
                    */}
                    {r.capacidade !== null && ` · ${explicarCapacidade(r.capacidade)}`}
                  </>
                )}
              </p>
            </div>
          );
        })}

        {/*
          🔴 Recusadas aparecem, contadas. Uma recomendação que o validador
          rejeitou é sinal de que algo está errado no cálculo — escondê-la
          deixaria o defeito invisível justamente para quem poderia notá-lo.
        */}
        {dados !== null && dados.recusadas.length > 0 && (
          <Aviso tipo="alerta">
            {dados.recusadas.length} recomendação(ões) foram recusadas pela validação e não aparecem
            acima.
            <details>
              <summary className="suave">Por quê</summary>
              <ul>
                {dados.recusadas.map((r) => (
                  <li key={r.problemId} className="suave">
                    {r.problemId}: {r.erros.map((e) => e.mensagem).join('; ')}
                  </li>
                ))}
              </ul>
            </details>
          </Aviso>
        )}
      </Painel>
    </div>
  );
}

/**
 * O nome interno da capacidade, dito para quem opera.
 *
 * 🔴 A tela imprimia `patch.read` e `script.execute` crus. São nomes do
 * registro de capacidades — corretos, e ilegíveis para a pessoa que precisa
 * decidir o que fazer com o problema.
 *
 * ⚠️ Capacidade desconhecida devolve o nome cru de propósito: inventar uma
 * frase amigável para algo que não está mapeado esconderia que o mapa ficou
 * para trás.
 */
function explicarCapacidade(nome: string): string {
  const FRASES: Record<string, string> = {
    'patch.read': 'depende de leitura de patches do KACE',
    'patch.execute': 'o KACE não aplica patch pela API',
    'script.execute': 'depende de um script homologado',
    'inventory.force': 'o produto pode forçar o inventário',
    'serviceDesk.write': 'depende de abrir chamado no Service Desk',
  };
  return FRASES[nome] ?? `depende de ${nome}`;
}

/**
 * Quantos dias tem o retrato.
 *
 * 🔴 A data estava na tela, em cinza, do mesmo tamanho do resto — e as
 * decisões saem daquela lista. Dois dias de atraso num parque que muda todo
 * dia é a diferença entre agir sobre o que existe e agir sobre o que existia.
 */
function idadeDoRetrato(dia: string): string {
  const quando = new Date(dia);
  if (Number.isNaN(quando.getTime())) return 'Retrato de data desconhecida';

  const dias = Math.floor((Date.now() - quando.getTime()) / 86_400_000);
  if (dias <= 0) return 'Retrato de hoje';
  if (dias === 1) return 'Retrato de ontem';
  return `⚠️ Retrato de ${dias} dias atrás`;
}
